import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';

import { formatLocaleDate } from '../../../common/utils';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the one-line recap of what
 * the flow has gathered so far. Deliberately quiet - secondary text, no border, no background - so
 * it reads as a caption under the step indicator rather than as another panel competing with the
 * step's own content.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:SHARED
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  list: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: 0,
    padding: theme.spacing(0, 3),
    listStyle: 'none',
  },
  /**
   * The separator is drawn on the item rather than inserted between items, so the list stays a flat
   * map over the entries - and it comes off the first item of every wrapped row on its own, which a
   * separate separator element cannot do.
   */
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:not(:first-child)::before': {
      content: '""',
      width: 3,
      height: 3,
      borderRadius: '50%',
      backgroundColor: theme.palette.text.disabled,
    },
  },
  value: {
    color: theme.palette.text.secondary,
  },
}));

/**
 * @function StepSummary
 * @description Recaps what the acquisition flow has gathered so far - satellite, area, how many
 * images, over which period - under the step indicator.
 * The flow can be arrived at with its state already filled: coming back from the result page, or
 * from a session import that never walked the steps at all. Until now the page looked identical to
 * a fresh start in both cases, so there was no way to tell a restored session from an empty one
 * without opening each step to look. Reading the same state the steps are gated on means the recap
 * cannot disagree with what the flow will actually let the user do.
 * Entries with nothing behind them are left out rather than shown empty, so the line grows as the
 * flow progresses and disappears entirely at the start.
 * @param {Object} props
 * @param {Object} [props.acquisition] - The module's acquisition state, as its selector returns it:
 *  `satellite`, `geometry`, `availableDates`, `start`, `end`. Shapes differ slightly between
 *  modules, so every field is treated as optional.
 * @param {Array<Array<Object>>} [props.imagePeriods] - The image selections a module confirms one
 *  period at a time (CSqueeze and EUSCoast). Those modules hand their chosen images to their own
 *  store and clear `availableDates`, so the recap had nothing left to count and went quiet about
 *  the imagery the moment it was actually chosen - the one thing the user had just spent the step
 *  deciding.
 * @param {String} [props.className] - Optional extra class for the wrapping element.
 * **Called by:
 * @see {AcquisitionPage} - each module's wizard, under its step indicator.
 * @memberof module:SHARED
 * @returns {JSX.Element|null} The recap, or nothing when there is nothing to recap yet.
 */
const StepSummary = ({ acquisition = {}, imagePeriods, className }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const { satellite, geometry, availableDates, start, end } = acquisition;

  /**
   * Every image confirmed across the periods, and the span they cover. The span is read from the
   * images rather than from `start`/`end`: those are the period that was *searched*, which on a
   * module that confirms several periods is only the last one looked at.
   */
  const selectedImages = useMemo(
    () => (imagePeriods || []).reduce((all, images) => all.concat(images || []), []),
    [imagePeriods],
  );
  const selectedSpan = useMemo(() => {
    const dates = selectedImages.map((image) => image?.date).filter(Boolean).sort();
    return dates.length ? { start: dates[0], end: dates[dates.length - 1] } : null;
  }, [selectedImages]);

  /**
   * The span the images themselves cover, for a module that never writes the searched period to the
   * store - Intertidal refines its image list without going through the period step, so `start` and
   * `end` stay empty there and the recap said nothing at all about when the images are from.
   * Only consulted once the stored period has had its say, so nothing changes where one exists.
   */
  const availableSpan = useMemo(() => {
    const dates = (availableDates || []).map((image) => image?.date).filter(Boolean).sort();
    return dates.length ? { start: dates[0], end: dates[dates.length - 1] } : null;
  }, [availableDates]);

  const entries = [];

  if (satellite?.name) {
    entries.push({ key: 'satellite', value: satellite.name });
  }

  if (geometry) {
    entries.push({ key: 'area', value: t('forms.core.stepSummary.area') });
  }

  const totalImages = availableDates?.length || selectedImages.length;
  if (totalImages) {
    entries.push({
      key: 'images',
      value: t(`forms.core.stepSummary.images.${totalImages === 1 ? 'singular' : 'plural'}`, { total: totalImages }),
    });
  }

  if (imagePeriods?.length > 1) {
    const total = imagePeriods.length;
    entries.push({ key: 'periods', value: t('forms.core.stepSummary.periods.plural', { total }) });
  }

  const span = selectedSpan || (start && end ? { start, end } : null) || availableSpan;
  if (span) {
    entries.push({ key: 'period', value: `${formatLocaleDate(span.start)} – ${formatLocaleDate(span.end)}` });
  }

  if (entries.length === 0) return null;

  return (
    <ul className={[classes.list, className].filter(Boolean).join(' ')} aria-label={t('forms.core.stepSummary.label')}>
      {entries.map((entry) => (
        <li key={entry.key} className={classes.item}>
          <Typography variant="caption" className={classes.value}>
            {entry.value}
          </Typography>
        </li>
      ))}
    </ul>
  );
};

export default StepSummary;
